// Unified Data Service for Silk Road Terminal
// Single interface for all data access with fallback to static data

import { worldBankApi, imfApi, fxApi } from '../api';
import type { APIResponse } from '../api/types';
import type {
  CountryOverview,
  GDPData,
  FDIData,
  PopulationData,
  InflationData,
  ReservesData,
  DebtData,
  TradeData,
  RatingsData,
} from '../../types/country';
import type { DataPoint, TimeSeries, PeerComparisonData } from '../../types/indicator';
import { cacheService, DEFAULT_TTL } from './cacheService';
import { getIndicatorById } from './indicatorRegistry';

/**
 * Static fallback data for Uzbekistan
 * Used ONLY when World Bank/IMF APIs are unavailable
 *
 * SOURCES:
 * - GDP/Macro: IMF WEO October 2024
 * - Population: UN Population Division 2024
 * - FDI: UNCTAD / Central Bank of Uzbekistan
 * - Credit Ratings: S&P, Fitch, Moody's official releases (Nov 2025)
 *
 * Last verified: 2024-12-15
 * IMPORTANT: This data should be periodically updated from official sources
 */

// Static time series data for key indicators (Uzbekistan)
// Used as fallback when APIs are unavailable
const STATIC_TIME_SERIES: Record<string, Array<{ year: number; value: number }>> = {
  // GDP in USD (billions converted to raw value)
  gdp: [
    { year: 2024, value: 105e9 },
    { year: 2023, value: 92e9 },
    { year: 2022, value: 80e9 },
    { year: 2021, value: 69e9 },
    { year: 2020, value: 60e9 },
    { year: 2019, value: 57e9 },
    { year: 2018, value: 50e9 },
  ],
  // GDP Growth (%)
  gdp_growth: [
    { year: 2024, value: 6.2 },
    { year: 2023, value: 6.3 },
    { year: 2022, value: 5.7 },
    { year: 2021, value: 7.4 },
    { year: 2020, value: 2.0 },
    { year: 2019, value: 5.7 },
    { year: 2018, value: 5.4 },
  ],
  // Inflation CPI (%)
  inflation_cpi: [
    { year: 2024, value: 9.0 },
    { year: 2023, value: 10.0 },
    { year: 2022, value: 11.4 },
    { year: 2021, value: 10.8 },
    { year: 2020, value: 11.1 },
    { year: 2019, value: 14.5 },
    { year: 2018, value: 17.5 },
  ],
  // FDI Inflows (USD)
  fdi_inflows: [
    { year: 2024, value: 11.9e9 },
    { year: 2023, value: 7.7e9 },
    { year: 2022, value: 5.1e9 },
    { year: 2021, value: 4.8e9 },
    { year: 2020, value: 2.4e9 },
    { year: 2019, value: 2.3e9 },
    { year: 2018, value: 0.6e9 },
  ],
  // FX Reserves (USD)
  reserves: [
    { year: 2024, value: 49.7e9 },
    { year: 2023, value: 42e9 },
    { year: 2022, value: 35e9 },
    { year: 2021, value: 34e9 },
    { year: 2020, value: 32e9 },
    { year: 2019, value: 29e9 },
    { year: 2018, value: 27e9 },
  ],
  // Population
  population: [
    { year: 2024, value: 37100000 },
    { year: 2023, value: 36500000 },
    { year: 2022, value: 35900000 },
    { year: 2021, value: 35300000 },
    { year: 2020, value: 34700000 },
    { year: 2019, value: 34100000 },
    { year: 2018, value: 33500000 },
  ],
  // Trade/GDP (%)
  trade_gdp: [
    { year: 2024, value: 59 },
    { year: 2023, value: 58 },
    { year: 2022, value: 61 },
    { year: 2021, value: 65 },
    { year: 2020, value: 57 },
    { year: 2019, value: 63 },
    { year: 2018, value: 68 },
  ],
  // External Debt/GDP (%)
  external_debt_gdp: [
    { year: 2024, value: 42 },
    { year: 2023, value: 41 },
    { year: 2022, value: 37 },
    { year: 2021, value: 39 },
    { year: 2020, value: 43 },
    { year: 2019, value: 41 },
    { year: 2018, value: 34 },
  ],
  // GDP per capita (USD)
  gdp_per_capita: [
    { year: 2024, value: 2830 },
    { year: 2023, value: 2520 },
    { year: 2022, value: 2230 },
    { year: 2021, value: 1955 },
    { year: 2020, value: 1730 },
    { year: 2019, value: 1670 },
    { year: 2018, value: 1490 },
  ],
};

// Static data for peer countries (Central Asia)
const STATIC_PEER_DATA: Record<string, Record<string, number>> = {
  gdp_growth: {
    UZB: 6.2, KAZ: 4.8, KGZ: 6.0, TJK: 7.5, TKM: 6.2, AZE: 2.5, GEO: 6.0, MNG: 5.0,
  },
  gdp: {
    UZB: 105e9, KAZ: 261e9, KGZ: 11e9, TJK: 12e9, TKM: 60e9, AZE: 78e9, GEO: 27e9, MNG: 18e9,
  },
  inflation_cpi: {
    UZB: 9.0, KAZ: 8.5, KGZ: 10.5, TJK: 4.0, TKM: 3.0, AZE: 2.0, GEO: 2.5, MNG: 7.0,
  },
  population: {
    UZB: 37.1e6, KAZ: 20e6, KGZ: 7e6, TJK: 10e6, TKM: 6.5e6, AZE: 10.5e6, GEO: 3.7e6, MNG: 3.4e6,
  },
  fdi_inflows: {
    UZB: 11.9e9, KAZ: 28e9, KGZ: 0.8e9, TJK: 0.1e9, TKM: 0.5e9, AZE: 7e9, GEO: 2e9, MNG: 2.5e9,
  },
  reserves: {
    UZB: 49.7e9, KAZ: 34e9, KGZ: 3e9, TJK: 2.5e9, TKM: 20e9, AZE: 16e9, GEO: 5e9, MNG: 4e9,
  },
  trade_gdp: {
    UZB: 59, KAZ: 65, KGZ: 95, TJK: 70, TKM: 45, AZE: 85, GEO: 110, MNG: 115,
  },
  external_debt_gdp: {
    UZB: 42, KAZ: 55, KGZ: 85, TJK: 65, TKM: 5, AZE: 20, GEO: 80, MNG: 200,
  },
};

const STATIC_UZB_DATA: CountryOverview = {
  country: {
    code: 'UZB',
    name: 'Uzbekistan',
    region: 'Central Asia',
    incomeLevel: 'Lower middle income',
    capitalCity: 'Tashkent',
    flag: '🇺🇿',
  },
  gdp: {
    value: 105.0,      // IMF WEO Oct 2024
    growth: 6.2,       // IMF WEO Oct 2024
    perCapita: 3500,   // World Bank WDI
    perCapitaPPP: 9500, // IMF WEO Oct 2024
  },
  fdi: {
    netInflows: 11.9,  // UNCTAD / Central Bank 2024
    netOutflows: 0.1,
    toGdp: 11.3,
    growth: 53.6,      // YoY growth 2024
  },
  population: {
    total: 37100000,   // UN Population Division 2024
    growth: 1.5,
    density: 83,
    urbanization: 48,
    medianAge: 27,
    laborForce: 25500000,
  },
  inflation: {
    cpi: 9.0,          // Central Bank of Uzbekistan
    ppi: null,
    core: null,
    target: 5.0,
  },
  reserves: {
    total: 49.7,       // Central Bank of Uzbekistan
    monthsImport: 10,
    toGdp: 47.3,
    toShortTermDebt: null,
  },
  debt: {
    publicDebt: 33,    // IMF WEO Oct 2024
    externalDebt: 42,
    shortTermDebt: null,
    debtService: null,
  },
  trade: {
    exports: 24.0,
    imports: 38.0,
    balance: -14.0,
    exportsGdp: 23.0,
    importsGdp: 36.0,
    currentAccount: -5.0, // IMF WEO Oct 2024
  },
  ratings: {
    // Updated ratings as of Nov 2025
    sp: 'BB',          // S&P upgraded Nov 21, 2025
    fitch: 'BB',       // Fitch upgraded Jun 26, 2025
    moodys: 'Ba3',     // Moody's Jun 2025
    outlook: 'Stable',
  },
  lastUpdated: new Date('2024-12-15'),
  dataSource: 'static',
};

/**
 * Get complete country overview with all key indicators
 */
export const getCountryOverview = async (
  countryCode: string = 'UZB'
): Promise<CountryOverview> => {
  const cacheKey = `overview_${countryCode}`;

  // Check cache first
  const cached = cacheService.get<CountryOverview>(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    // Fetch profile from World Bank
    const profileResponse = await worldBankApi.fetchCountryProfile(countryCode);

    if (profileResponse.data) {
      const profile = profileResponse.data;

      const overview: CountryOverview = {
        country: {
          code: countryCode,
          name: countryCode === 'UZB' ? 'Uzbekistan' : countryCode,
          region: 'Central Asia',
          incomeLevel: 'Lower middle income',
          capitalCity: 'Tashkent',
        },
        gdp: {
          value: profile.gdp ? profile.gdp / 1e9 : null,
          growth: profile.gdpGrowth,
          perCapita: profile.gdpPerCapita,
          perCapitaPPP: null,
        },
        fdi: {
          netInflows: profile.fdiInflows ? profile.fdiInflows / 1e9 : null,
          netOutflows: null,
          toGdp: profile.fdiGdp,
          growth: null,
        },
        population: {
          total: profile.population,
          growth: profile.populationGrowth,
          density: null,
          urbanization: null,
          medianAge: null,
          laborForce: null,
        },
        inflation: {
          cpi: profile.inflation,
          ppi: null,
          core: null,
          target: null,
        },
        reserves: {
          total: profile.reserves ? profile.reserves / 1e9 : null,
          monthsImport: null,
          toGdp: null,
          toShortTermDebt: null,
        },
        debt: {
          publicDebt: profile.govtDebtGdp,
          externalDebt: profile.externalDebt ? profile.externalDebt / 1e9 : null,
          shortTermDebt: null,
          debtService: null,
        },
        trade: {
          exports: null,
          imports: null,
          balance: null,
          exportsGdp: profile.exportsGdp,
          importsGdp: profile.importsGdp,
          currentAccount: profile.currentAccount,
        },
        ratings: {
          sp: countryCode === 'UZB' ? 'BB-' : null,
          fitch: countryCode === 'UZB' ? 'BB-' : null,
          moodys: countryCode === 'UZB' ? 'B1' : null,
          outlook: 'Stable',
        },
        lastUpdated: new Date(),
        dataSource: profileResponse.isStale ? 'cached' : 'live',
      };

      // Cache the result
      cacheService.set(cacheKey, overview, DEFAULT_TTL);

      return overview;
    }

    throw new Error('No data received from API');
  } catch (error) {
    console.warn('Failed to fetch country overview, using static data:', error);

    // Return static data for Uzbekistan
    if (countryCode === 'UZB') {
      return { ...STATIC_UZB_DATA };
    }

    // Return minimal static data for other countries
    return {
      ...STATIC_UZB_DATA,
      country: {
        code: countryCode,
        name: countryCode,
        region: 'Unknown',
        incomeLevel: 'Unknown',
        capitalCity: 'Unknown',
      },
      dataSource: 'static',
    };
  }
};

/**
 * Get time series data for a specific indicator
 */
export const getMacroTimeSeries = async (
  countryCode: string,
  indicatorId: string,
  years: number = 10
): Promise<TimeSeries> => {
  const cacheKey = `timeseries_${countryCode}_${indicatorId}_${years}`;

  // Check cache
  const cached = cacheService.get<TimeSeries>(cacheKey);
  if (cached) {
    return cached;
  }

  const metadata = getIndicatorById(indicatorId);

  try {
    // Try World Bank first
    const wbResponse = await worldBankApi.fetchTimeSeries(countryCode, indicatorId, years);

    if (wbResponse.data && wbResponse.data.length > 0) {
      const timeSeries: TimeSeries = {
        indicatorId,
        countryCode,
        data: wbResponse.data.map(d => ({
          year: d.year,
          value: d.value,
          source: 'worldbank' as const,
        })),
        metadata: metadata || undefined,
        lastUpdated: new Date(),
      };

      cacheService.set(cacheKey, timeSeries, DEFAULT_TTL);
      return timeSeries;
    }

    // Try IMF as fallback
    const imfResponse = await imfApi.fetchIndicator(countryCode, indicatorId);

    if (imfResponse.data && imfResponse.data.length > 0) {
      const currentYear = new Date().getFullYear();
      const filteredData = imfResponse.data.filter(d => d.year >= currentYear - years);

      const timeSeries: TimeSeries = {
        indicatorId,
        countryCode,
        data: filteredData.map(d => ({
          year: d.year,
          value: d.value,
          isForecast: d.isForecast,
          source: 'imf' as const,
        })),
        metadata: metadata || undefined,
        lastUpdated: new Date(),
      };

      cacheService.set(cacheKey, timeSeries, DEFAULT_TTL);
      return timeSeries;
    }

    throw new Error('No data available from any source');
  } catch (error) {
    console.warn(`Failed to fetch time series for ${indicatorId}:`, error);

    // Fall back to static data for Uzbekistan
    if (countryCode === 'UZB' && STATIC_TIME_SERIES[indicatorId]) {
      const staticData = STATIC_TIME_SERIES[indicatorId];
      const currentYear = new Date().getFullYear();
      const filteredData = staticData.filter(d => d.year >= currentYear - years);

      return {
        indicatorId,
        countryCode,
        data: filteredData.map(d => ({
          year: d.year,
          value: d.value,
          source: 'static' as const,
        })),
        metadata: metadata || undefined,
        lastUpdated: new Date(),
      };
    }

    // Return empty time series for other countries
    return {
      indicatorId,
      countryCode,
      data: [],
      metadata: metadata || undefined,
      lastUpdated: new Date(),
    };
  }
};

/**
 * Get peer comparison data for multiple countries
 */
export const getPeerComparison = async (
  countryCodes: string[],
  indicatorIds: string[],
  year?: number
): Promise<Record<string, PeerComparisonData>> => {
  const targetYear = year || new Date().getFullYear() - 1;
  const cacheKey = `peer_${countryCodes.join('_')}_${indicatorIds.join('_')}_${targetYear}`;

  // Check cache
  const cached = cacheService.get<Record<string, PeerComparisonData>>(cacheKey);
  if (cached) {
    return cached;
  }

  const result: Record<string, PeerComparisonData> = {};

  try {
    // Fetch each indicator for all countries
    await Promise.all(
      indicatorIds.map(async indicatorId => {
        // Try World Bank first
        const wbResponse = await worldBankApi.fetchMultipleCountries(
          countryCodes,
          worldBankApi.getIndicatorCode(indicatorId) || indicatorId,
          targetYear
        );

        if (wbResponse.data) {
          const values: Record<string, number | null> = {};
          const rankings: Record<string, number> = {};

          for (const code of countryCodes) {
            values[code] = wbResponse.data[code] ?? null;
          }

          // Calculate rankings
          const sortedCountries = countryCodes
            .filter(code => values[code] !== null)
            .sort((a, b) => (values[b] ?? 0) - (values[a] ?? 0));

          sortedCountries.forEach((code, index) => {
            rankings[code] = index + 1;
          });

          result[indicatorId] = {
            indicatorId,
            year: targetYear,
            values,
            rankings,
          };
        }
      })
    );

    cacheService.set(cacheKey, result, DEFAULT_TTL);
    return result;
  } catch (error) {
    console.warn('Failed to fetch peer comparison:', error);

    // Fall back to static peer data
    for (const indicatorId of indicatorIds) {
      if (STATIC_PEER_DATA[indicatorId]) {
        const values: Record<string, number | null> = {};
        const rankings: Record<string, number> = {};

        for (const code of countryCodes) {
          values[code] = STATIC_PEER_DATA[indicatorId][code] ?? null;
        }

        // Calculate rankings
        const sortedCountries = countryCodes
          .filter(code => values[code] !== null)
          .sort((a, b) => (values[b] ?? 0) - (values[a] ?? 0));

        sortedCountries.forEach((code, index) => {
          rankings[code] = index + 1;
        });

        result[indicatorId] = {
          indicatorId,
          year: targetYear,
          values,
          rankings,
        };
      }
    }

    return result;
  }
};

/**
 * Get forecasts for an indicator from IMF
 */
export const getForecasts = async (
  countryCode: string,
  indicatorId: string,
  yearsAhead: number = 5
): Promise<DataPoint[]> => {
  try {
    const response = await imfApi.fetchForecasts(countryCode, indicatorId, yearsAhead);

    if (response.data) {
      return response.data.forecast;
    }

    return [];
  } catch (error) {
    console.warn(`Failed to fetch forecasts for ${indicatorId}:`, error);
    return [];
  }
};

/**
 * Get the latest value for an indicator
 */
export const getLatestValue = async (
  countryCode: string,
  indicatorId: string
): Promise<{ value: number | null; year: number | null; source: string }> => {
  try {
    const result = await worldBankApi.fetchLatestValue(countryCode, indicatorId);
    return {
      ...result,
      source: 'worldbank',
    };
  } catch (error) {
    console.warn(`Failed to fetch latest value for ${indicatorId}:`, error);
    return { value: null, year: null, source: 'none' };
  }
};

/**
 * Get exchange rate data
 */
export const getExchangeRate = async (
  base: string = 'USD',
  target: string = 'UZS'
): Promise<{
  rate: number | null;
  change24h: number | null;
  history: Array<{ date: string; rate: number }>;
}> => {
  try {
    const [rateResponse, historyResponse] = await Promise.all([
      fxApi.fetchRate(base, target),
      fxApi.fetchHistory(base, target, 30),
    ]);

    return {
      rate: rateResponse.data?.rate ?? null,
      change24h: rateResponse.data?.change24h ?? null,
      history: historyResponse.data?.data ?? [],
    };
  } catch (error) {
    console.warn('Failed to fetch exchange rate:', error);
    return { rate: null, change24h: null, history: [] };
  }
};

/**
 * Check health of all data sources
 */
export const checkDataSourcesHealth = async (): Promise<{
  worldBank: boolean;
  imf: boolean;
  fx: boolean;
}> => {
  const [wbHealth, imfHealth, fxHealth] = await Promise.all([
    worldBankApi.checkAPIHealth(),
    imfApi.checkAPIHealth(),
    fxApi.checkAPIHealth(),
  ]);

  return {
    worldBank: wbHealth,
    imf: imfHealth,
    fx: fxHealth,
  };
};

/**
 * Refresh all cached data
 */
export const refreshAllData = async (countryCode: string = 'UZB'): Promise<void> => {
  // Clear cache
  cacheService.clear();

  // Pre-fetch key data
  await Promise.all([
    getCountryOverview(countryCode),
    getMacroTimeSeries(countryCode, 'gdp_growth', 10),
    getMacroTimeSeries(countryCode, 'inflation_cpi', 10),
    getMacroTimeSeries(countryCode, 'fdi_inflows', 10),
  ]);
};

// Export default object
const dataService = {
  getCountryOverview,
  getMacroTimeSeries,
  getPeerComparison,
  getForecasts,
  getLatestValue,
  getExchangeRate,
  checkDataSourcesHealth,
  refreshAllData,
};

export default dataService;
